import type { RenderTriggerArgs, ListProps } from '@mfw/components';

import { Autocomplete, Iconify, useBooleanState } from '@mfw/components';
import IconButton from '@mui/material/IconButton';
import { useIntl } from 'react-intl';
import React from 'react';

import type { Routes } from '../../router';

import { buildAccountBillingRoute } from '../AccountBilling';
import {
  useOrganizationContext,
  usePermissionsContext,
  useAuthContext,
} from '../../context';
import useLogicalProfiles from '../../hooks/users/useLogicalProfiles';
import switchProfileMessages from '../SwitchProfileModal/messages';
import SwitchProfileModal from '../SwitchProfileModal';
import { useNavigate } from '../../router/hooks';
import ownMessages from './messages';
import messages from '../../messages';
import './SettingsDropdown.scss';

// Sentinel value for the "Switch profile" row — opens the modal instead of
// navigating, unlike every other item in this dropdown.
const SWITCH_PROFILE_VALUE = '__switch_profile__' as Routes;
const BACK_TO_WORKSPACE_VALUE = '__back_to_workspace__' as Routes;

const SettingsDropdown = () => {
  const { formatMessage } = useIntl();
  const { currentUser, signOutAsync } = useAuthContext();
  const {
    currentCorpOrganization,
    currentOrganization,
    isPersonalMode,
    selectPersonalProfile,
  } = useOrganizationContext();
  const { hasPermission } = usePermissionsContext();
  const navigate = useNavigate();
  const switchProfileModal = useBooleanState();

  const organizationId = currentOrganization?.id ?? '';
  const canReadFinance =
    hasPermission('billing_read') || hasPermission('finance_stats_read');
  const isPlatformUser = Boolean(currentUser?.isPlatformUser);
  // P1: a platform user is never an organization member — nothing to count.
  const logicalProfiles = useLogicalProfiles(!isPlatformUser);
  const orgMembershipCount = (logicalProfiles.data ?? []).filter(
    (profile) => profile.organizationId !== null,
  ).length;
  // The entry normally needs 2+ organization profiles. The other cases are
  // contexts this modal is the ONLY way out of:
  //  - a platform user never gets the modal: the tenant can hold any number
  //    of organizations, so they are entered from the admin Organizations
  //    list instead, and `showBackToWorkspace` below is the way out;
  //  - a corp link: corp mode is not a membership, and it is the default
  //    landing context without a last-used organization;
  //  - the personal profile with an organization: `lastUsedContext:
  //    'personal'` persists server-side, and nothing else links into an org.
  const showSwitchProfile =
    !isPlatformUser &&
    (orgMembershipCount > 1 ||
      Boolean(currentCorpOrganization?.id) ||
      (isPersonalMode && orgMembershipCount > 0));
  // P4/P6: a platform user drilled into an organization (via the admin list,
  // or its URL) returns to the org-less curation workspace from here.
  const showBackToWorkspace =
    isPlatformUser && Boolean(currentOrganization?.id);
  // Everyone who doesn't qualify for the switch above has at most one
  // organization and nothing else to switch between — offer the same
  // "Add profile" entry point the modal itself ends with instead. P1: a
  // platform user holds no membership, so it never gets one.
  const showAddProfile = !showSwitchProfile && !isPlatformUser;
  // P2 — a platform user is never a payer: `hasPermission` answers `true` for
  // it regardless (constraint 17), so this item needs the explicit class
  // check the sidebar's `canReadWallet` also carries.
  const showBillingSubscriptions =
    Boolean(currentOrganization?.id) &&
    canReadFinance &&
    !currentUser?.isPlatformUser;
  // T19 — points at the new Account settings → Subscriptions area instead of
  // the legacy `FinancePage` (which now redirects here itself for a regular
  // user anyway — this just skips that hop).
  const billingSubscriptionsRoute = buildAccountBillingRoute({
    userId: currentUser?.id ?? '',
    scope: organizationId,
    tab: 'overview',
  });

  const options = React.useMemo(() => {
    return [
      ...(showSwitchProfile
        ? [
            {
              disabled: !currentUser?.hasFinishedOnboarding,
              label: formatMessage(ownMessages.switchProfile),
              value: SWITCH_PROFILE_VALUE,
              'data-testid': 'settings-switch-profile',
            },
          ]
        : showBackToWorkspace
          ? [
              {
                label: formatMessage(ownMessages.backToWorkspace),
                value: BACK_TO_WORKSPACE_VALUE,
                'data-testid': 'settings-back-to-workspace',
              },
            ]
          : showAddProfile
            ? [
                {
                  disabled: !currentUser?.hasFinishedOnboarding,
                  label: formatMessage(switchProfileMessages.addProfile),
                  value: '/profiles/new' as Routes,
                  'data-testid': 'settings-add-profile',
                },
              ]
            : []),
      {
        disabled: !currentUser?.hasFinishedOnboarding,
        label: formatMessage(ownMessages.accountSettings),
        value: `/me/${currentUser?.id}` as Routes,
      },
      ...(showBillingSubscriptions
        ? [
            {
              disabled: !currentUser?.hasFinishedOnboarding,
              label: formatMessage(ownMessages.billingSubscriptions),
              value: billingSubscriptionsRoute as Routes,
              'data-testid': 'settings-billing-subscriptions',
            },
          ]
        : []),
      {
        destructive: true,
        divider: true,
        label: formatMessage(messages.logout),
        value: '/auth/login' as Routes,
        'data-testid': 'settings-logout',
      },
    ] as ListProps['items'];
  }, [
    currentUser?.hasFinishedOnboarding,
    currentUser?.id,
    formatMessage,
    showSwitchProfile,
    showBackToWorkspace,
    showAddProfile,
    showBillingSubscriptions,
    billingSubscriptionsRoute,
  ]);

  const renderTrigger = React.useCallback(
    ({ getReferenceProps, refs }: RenderTriggerArgs) => {
      return (
        <IconButton
          ref={(e) => {
            refs.setReference(e);
          }}
          data-testid="settings-dropdown-button"
          {...getReferenceProps()}
        >
          <Iconify
            className="shadow-2xl rounded-lg"
            icon="mdi:settings"
            size="large"
          />
        </IconButton>
      );
    },
    [],
  );

  const handleSelected = React.useCallback(
    async (selected: ListProps['items']) => {
      const value = selected?.[0].value as Routes;
      if (value === SWITCH_PROFILE_VALUE) {
        switchProfileModal.on();
        return;
      }
      if (value === BACK_TO_WORKSPACE_VALUE) {
        selectPersonalProfile();
        navigate('/');
        return;
      }
      if (value === '/auth/login') {
        await signOutAsync();
      }
      navigate(value);
    },
    [switchProfileModal, signOutAsync, navigate, selectPersonalProfile],
  );
  return (
    <>
      <Autocomplete
        classes={{
          dropdownMenu: {
            root: 'Mfw-SettingsDropdown-dropdownMenu__root',
          },
        }}
        title={formatMessage(messages.settings)}
        renderTrigger={renderTrigger}
        onSelected={handleSelected}
        withoutSelectedHighlight
        total={options.length}
        options={options}
        multiple={false}
        withoutDeselect
        withoutSearch
      />
      <SwitchProfileModal switchProfileModal={switchProfileModal} />
    </>
  );
};

export default SettingsDropdown;
